/**
 * v2 servicing Slice 7: full borrower conversation view for one portal
 * request — reply, archive/unarchive, load-older (cursor), and the
 * viewed-watermark advance. Extracted from MyRequestsCard to keep both
 * files under the project's readability threshold.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  customerPortalApi,
  PortalMessage,
  PortalRequest,
} from '../../../services/customerPortalApi';
import { generateUuid } from '../../../utils/uuid';
import { PORTAL_COPY, PortalLanguage } from '../portalCopy';

interface Props {
  request: PortalRequest;
  language?: PortalLanguage;
  onArchiveToggle: () => void;
  onViewed: () => void;
  onMessageSent: () => void;
}

const RequestThread: React.FC<Props> = ({
  request,
  language = 'en',
  onArchiveToggle,
  onViewed,
  onMessageSent,
}) => {
  const copy = PORTAL_COPY[language];
  const [messages, setMessages] = useState<PortalMessage[]>(request.messages ?? []);
  const [hasMoreOlder, setHasMoreOlder] = useState((request.messages?.length ?? 0) >= 20);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const pendingClientMessageId = useRef<string | null>(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    if (request.unread && request.last_message_id) {
      customerPortalApi
        .markViewed(request.id, request.last_message_id)
        .then(onViewed)
        .catch(() => {
          /* best-effort — the badge simply stays until the next successful view */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id]);

  const loadOlder = async () => {
    if (messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const older = await customerPortalApi.getRequestMessages(request.id, messages[0].id, 20);
      setMessages((prev) => [...[...older].reverse(), ...prev]);
      setHasMoreOlder(older.length === 20);
    } finally {
      setLoadingOlder(false);
    }
  };

  const sendReply = async () => {
    const body = replyBody.trim();
    if (!body) return;
    setSubmitting(true);
    setError(null);
    const clientMessageId = pendingClientMessageId.current ?? generateUuid();
    pendingClientMessageId.current = clientMessageId;
    try {
      const msg = await customerPortalApi.postRequestMessage(request.id, body, clientMessageId);
      setMessages((prev) => [...prev, msg]);
      setReplyBody('');
      pendingClientMessageId.current = null;
      // keep the parent's cached list in sync so a collapse/re-expand of this
      // thread (which remounts from the parent's `request.messages`) doesn't
      // lose the just-sent reply.
      onMessageSent();
    } catch {
      setError(copy.threadReplyError);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleArchive = async () => {
    setArchiving(true);
    try {
      if (request.archived_by_borrower_at) {
        await customerPortalApi.unarchiveRequest(request.id);
      } else {
        await customerPortalApi.archiveRequest(request.id);
      }
      onArchiveToggle();
    } finally {
      setArchiving(false);
    }
  };

  const authorLabel = (m: PortalMessage) =>
    m.is_backfilled
      ? copy.threadHistorical
      : m.author_type === 'borrower'
        ? copy.threadYou
        : copy.threadDealership;

  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
      {hasMoreOlder && (
        <button
          type="button"
          onClick={loadOlder}
          disabled={loadingOlder}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          {copy.threadLoadOlder}
        </button>
      )}
      <ul className="space-y-2">
        {messages.map((m) => (
          <li
            key={m.id}
            className={`rounded-lg px-3 py-2 text-sm ${
              m.is_backfilled
                ? 'bg-gray-50 text-gray-500 italic'
                : m.author_type === 'borrower'
                  ? 'bg-blue-50 text-gray-800'
                  : 'bg-gray-50 text-gray-800'
            }`}
          >
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span className="font-medium">{authorLabel(m)}</span>
              <span>{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p>{m.body}</p>
          </li>
        ))}
      </ul>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex flex-col sm:flex-row gap-2">
        <textarea
          rows={2}
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder={copy.threadReplyPlaceholder}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={sendReply}
          disabled={submitting || !replyBody.trim()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 self-start"
        >
          {copy.threadSend}
        </button>
      </div>
      <button
        type="button"
        onClick={toggleArchive}
        disabled={archiving}
        className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
      >
        {request.archived_by_borrower_at ? copy.threadUnarchive : copy.threadArchive}
      </button>
    </div>
  );
};

export default RequestThread;
